/**
 * Compute → Clusters (D447): the Kubernetes clusters the organisation has approved. Approving a cluster is what lets work
 * run on its node pools; the organisation's policy lists exactly these and is rewritten whenever one is added, changed or
 * removed. A row names the kind, where it is, its node pools with their tags and runtime classes, the projects it serves
 * and whose sign-in the work runs as.
 */

import type { ApprovedCluster } from '@aioe/shared';
import { CircleQuestion, Layers3Diagonal, Server } from '@gravity-ui/icons';
import { useState } from 'react';

import { computeRoutes, problemText, routes } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Button, ConfirmDialog, Empty, ErrorNote, ListGroup, ListRow, PageColumn, PageHeader, Stat } from '../ui.tsx';
import { Explainer } from '../ui/Layout.tsx';
import { RowMenu } from '../ui/RowMenu.tsx';
import { ClusterForm } from './ClusterForm.tsx';
import { KIND_WORDS, plural, poolWords, runsAs, TONE, whereWords } from './computeWords.ts';

export function ClustersPanel({ mayEdit }: { mayEdit: boolean }) {
  const list = useLoad(() => computeRoutes.clusters(), [], 60_000);
  const requests = useLoad(() => computeRoutes.requests().catch(() => undefined), [], 60_000);
  const projects = useLoad(() => routes.projects().catch(() => []), []);
  const [form, setForm] = useState<{ cluster?: ApprovedCluster }>();
  const [removing, setRemoving] = useState<ApprovedCluster>();
  const [error, setError] = useState<string>();
  const clusters = list.data?.clusters;
  const waiting = requests.data?.requests.filter((q) => q.state === 'pending') ?? [];
  const pools = (clusters ?? []).reduce((n, c) => n + (c.nodePools?.length ?? 0), 0);
  const latest = (clusters ?? []).map((c) => c.updatedAt).sort().at(-1);
  const projectName = (id: string) => (projects.data ?? []).find((p) => p.id === id)?.name ?? id;

  return (
    <PageColumn width="data">
      <PageHeader
        tone={TONE}
        icon={<Server />}
        title="Clusters"
        subtitle="The Kubernetes clusters work may run on, their node pools, and the projects each serves."
        actions={
          mayEdit ? (
            <Button size="sm" variant="primary" onPress={() => setForm({})}>
              Add cluster
            </Button>
          ) : undefined
        }
      />
      <Explainer id="compute-clusters" tone={TONE} icon={<Server />} title="How clusters work" summary="Approving a cluster is what lets work run on its node pools">
        <p>A cluster is a place agents can run in a sandbox, on nodes that join one of its node pools. The platform keeps the cluster’s address and its pools, never a kubeconfig, a token or a certificate: the work signs in as each person, or as the organisation with a secret kept under Secrets.</p>
        <p>When a project needs its agents sandboxed on a cluster, its work goes only to nodes of a cluster that serves it, in a pool that offers the isolation class its data needs. A node made for one project never takes another project’s work.</p>
        <p>The organisation’s policy lists exactly the clusters here. Adding, changing or removing one rewrites that list for every workbench.</p>
      </Explainer>
      <ErrorNote error={list.error ?? error} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<Server />} label="Clusters" value={clusters?.length} detail={clusters ? (latest ? `Last changed ${timeAgo(latest)}` : 'None approved yet') : ''} />
        <Stat tone={TONE} icon={<Layers3Diagonal />} label="Node pools" value={clusters ? pools : undefined} detail="Where nodes can join" />
        <Stat tone={TONE} icon={<CircleQuestion />} label="Requests waiting" value={requests.data ? waiting.length : undefined} detail={waiting.length ? 'People asking for more' : 'Nothing waiting'} needsYou={waiting.length > 0} />
      </div>
      <ListGroup
        title="Approved clusters"
        count={clusters?.length}
        description="Work may run on the node pools listed on each. Removing a cluster takes them away."
        empty={
          clusters && clusters.length === 0 ? (
            <Empty
              tone={TONE}
              icon={<Server />}
              title="No clusters approved yet"
              body="Add a cluster and its node pools, then say which projects it serves. Until then no work is placed on a cluster."
              action={
                mayEdit ? (
                  <Button size="sm" variant="primary" onPress={() => setForm({})}>
                    Add cluster
                  </Button>
                ) : undefined
              }
            />
          ) : undefined
        }
      >
        {!clusters && !list.error ? <ListRow title="Reading the clusters…" /> : null}
        {(clusters ?? []).map((c) => {
          const served = c.projectIds ?? [];
          const lines = poolWords(c);
          return (
            <ListRow
              key={c.id}
              icon={<Server />}
              title={c.name}
              description={
                <>
                  <span className="block">
                    {KIND_WORDS[c.kind]} · {whereWords(c)}
                  </span>
                  <span className="block">
                    {plural(c.nodePools?.length ?? 0, 'node pool')} · {runsAs(c.identity)}
                    {c.organisationSecret ? ` with the secret ${c.organisationSecret}` : ''}
                  </span>
                  {lines.length ? <span className="block">Pools: {lines.join('; ')}</span> : null}
                  <span className="block truncate">{served.length ? `Serves ${served.map(projectName).join(', ')}` : 'Serves any project'}</span>
                </>
              }
              compactControl
              control={
                mayEdit ? (
                  <RowMenu
                    label={`More for ${c.name}`}
                    items={[
                      { id: 'edit', label: 'Edit…' },
                      { id: 'remove', label: 'Remove…', danger: true },
                    ]}
                    onAction={(id) => (id === 'edit' ? setForm({ cluster: c }) : setRemoving(c))}
                  />
                ) : undefined
              }
            />
          );
        })}
      </ListGroup>

      {form ? (
        <ClusterForm
          cluster={form.cluster}
          onClose={() => setForm(undefined)}
          onSaved={() => {
            setForm(undefined);
            list.reload();
          }}
        />
      ) : null}
      <ConfirmDialog
        isOpen={Boolean(removing)}
        title={`Remove ${removing?.name ?? ''}?`}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={async () => {
          const c = removing!;
          setRemoving(undefined);
          setError(undefined);
          try {
            await computeRoutes.removeCluster(c.id);
            list.reload();
          } catch (e) {
            setError(problemText(e));
          }
        }}
      >
        Work can no longer be placed on this cluster, and the organisation’s policy is rewritten without it. Nodes that joined its pools stay until you remove them.
      </ConfirmDialog>
    </PageColumn>
  );
}
